import useSWR from "swr";
import { AlertTriangle, X } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

const fetcher = () => api.get("/forecast").then((r) => r.data);

// Threshold definitions
const THRESHOLDS = {
  heavyRain3h: 5,     // mm in 3 hours
  extremeHot: 32,     // °C — abbassato da 35 dato il clima estivo tipico locale
  extremeCold: 0,     // °C
  strongWind: 60,     // km/h
};

function detectAlerts(hourly = []) {
  const next24 = hourly.slice(0, 8); // 8 x 3h = 24h
  const alerts = [];
  const maxRain = Math.max(0, ...next24.map((h) => h.rain || 0));
  const maxTemp = Math.max(...next24.map((h) => h.temp));
  const minTemp = Math.min(...next24.map((h) => h.temp));
  const maxWind = Math.max(0, ...next24.map((h) => h.wind || 0));

  if (maxRain >= THRESHOLDS.heavyRain3h) alerts.push({ kind: "rain", label: `Pioggia intensa prevista fino a ${maxRain} mm/3h nelle prossime 24 ore` });
  if (maxTemp >= THRESHOLDS.extremeHot) alerts.push({ kind: "heat", label: `Caldo estremo previsto: massima fino a ${Math.round(maxTemp)}°C` });
  if (minTemp <= THRESHOLDS.extremeCold) alerts.push({ kind: "cold", label: `Gelo previsto: minima fino a ${Math.round(minTemp)}°C` });
  if (maxWind >= THRESHOLDS.strongWind) alerts.push({ kind: "wind", label: `Vento forte previsto: raffiche fino a ${Math.round(maxWind)} km/h` });

  return alerts;
}

export default function AlertBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useSWR("forecast", fetcher, { refreshInterval: 30 * 60_000 });

  if (dismissed) return null;
  const alerts = detectAlerts(data?.hourly ?? []);
  if (alerts.length === 0) return null;

  // Pick most severe: rain > wind > heat > cold order
  const priority = { rain: 4, wind: 3, heat: 2, cold: 1 };
  const primary = [...alerts].sort((a, b) => priority[b.kind] - priority[a.kind])[0];
  const isHot = primary.kind === "heat";
  const bg = isHot ? "bg-amber-500" : "bg-red-600";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid="alert-banner"
      className="fixed bottom-6 right-6 z-[60] max-w-sm"
      role="alert"
    >
      <div className={`${bg} text-white rounded-2xl shadow-2xl overflow-hidden`}>
        <div className="p-4 flex items-start gap-3">
          <span className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.24em] font-semibold opacity-90">Allerta meteo · prossime 24h</p>
            <p className="mt-1.5 text-sm leading-snug">{primary.label}</p>
            {alerts.length > 1 && (
              <ul className="mt-2 space-y-0.5 text-xs opacity-90">
                {alerts.filter(a => a !== primary).map((a) => (
                  <li key={a.kind}>· {a.label}</li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="alert-dismiss"
            className="flex-shrink-0 h-7 w-7 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
            aria-label="Chiudi allerta"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
